// Third-party
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';

// Components
import classNames from 'classnames';
import { Button } from '@capintel/component-lib/button';
import { PrimaryNavigation } from '../navigation';
import DelegateBanner from '../../containers/auth/DelegateBanner';
import SkipContentLink from '../misc/SkipContentLink';
import { AnnouncementBanner, AnnouncementBell } from '../../containers/announcements';
import UserDropdown from '../navigation/UserDropdown';
import HeaderBurgerMenuContainer from './HeaderBurgerMenuContainer';
import useScreenSize from '@/hooks/useScreenSize';

// Enums
import { FEATURE_FLAGS } from '@/enums/FeatureFlags';

// Images
import { ReactComponent as CapintelLogo } from '../../../images/capintel-logo.svg?react';
import { ReactComponent as CapintelLogoSmall } from '../../../images/capintel-logo-small.svg?react';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { NAV_PATHS } from '../../../utils/constants';
import { userHasPermission, PERMISSIONS, userHasAdminPermission } from '../../../utils/permissions';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { CurrentContact } from '../../../redux/modules/types/contacts';
import { setLocale } from '../../../redux/modules/intl';
import { selectDelegateMode } from '../../../redux/modules/auth/selectors';
import { logOpenHelpCenter } from '../../../services/usage-log';

// Type
import AppMode from '@/types/app-mode';
import Locale from '@/types/locale';
import NavigationItem from '@/types/navigation-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import useThemeVersion from '@/hooks/useThemeVersion';
import { Household } from '@/types/households';

// Legacy Component
import LegacyHeader from './LegacyHeader';

type StateProps = {
  contact?: Nullable<CurrentContact>;
  contextHouseholdId?: string;
  household?: Nullable<Household>;
  delegateMode?: boolean;
  locale: Locale;
  user?: Nullable<User>;
};

type DispatchProps = {
  setLocale: (newLocale: Locale) => void;
  logOpenHelpCenter: () => Promise<void>;
};

type Props = RouteComponentProps & DispatchProps & StateProps;

const HeaderV2: React.FC<Props> = props => {
  const { user, contact, household, contextHouseholdId, delegateMode, locale, history, location, match, logOpenHelpCenter } = props;
  const { isDesktop } = useScreenSize();
  const { isFFEnabled } = useFeatureFlag();

  const openHelpLink = useCallback(() => {
    logOpenHelpCenter();
    window.open(`${import.meta.env.REACT_APP_HELP_SITE_URL}/${locale.toLocaleLowerCase()}`, '_blank');
  }, [logOpenHelpCenter, locale]);

  const getContactHeaderTitle = useCallback(() => {
    if (user && userHasPermission(user, PERMISSIONS.BMO_GIF)) {
      return formatMessage('SiteNavigation.Clients');
    }
    return formatMessage('SiteNavigation.Contacts');
  }, [user]);

  const getContactsMenuPath = useCallback(
    (user: User) => {
      let navPath: string = NAV_PATHS.CONTACTS;
      if (user.appConfig.mode === AppMode.CONTACT) {
        const contactId: string | undefined = contact ? contact.contact._id : undefined;
        const householdId: string | undefined = household ? household.id : undefined;
        if (householdId || contextHouseholdId) {
          navPath = NAV_PATHS.HOUSEHOLD_DETAILS.replace(':householdId', householdId || contextHouseholdId || '');
        } else if (contactId) {
          navPath = NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contactId);
        } else {
          navPath = NAV_PATHS.CONTACTS;
        }
      }
      return navPath;
    },
    [household, contextHouseholdId, contact],
  );

  const getLeftMenuItems = useCallback(
    (user: User): NavigationItem[] => {
      const showModelsBasedOnPermission = () => {
        if (!userHasPermission(user, PERMISSIONS.USER_MODELS) && !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
          return NAV_PATHS.CUSTOM_BENCHMARKS;
        }
        if (!userHasPermission(user, PERMISSIONS.USER_MODELS)) {
          return NAV_PATHS.ENTERPRISE_MODELS;
        }
        return NAV_PATHS.MODELS;
      };

      const hasScreener = isFFEnabled(FEATURE_FLAGS.ENHANCED_SCREENER_ENABLED) && userHasPermission(user, PERMISSIONS.ENHANCED_SCREENER);

      const items: NavigationItem[] = [
        {
          label: formatMessage('SiteNavigation.Comparisons'),
          value: NAV_PATHS.COMPARISONS,
          hidden: !userHasPermission(user, PERMISSIONS.SECTION_COMPARISONS),
        },
        {
          label: getContactHeaderTitle(),
          value: getContactsMenuPath(user),
          hidden: !userHasPermission(user, PERMISSIONS.SECTION_CONTACTS),
        },
        {
          label: hasScreener ? formatMessage('SiteNavigation.Screener') : formatMessage('SiteNavigation.Search'),
          value: NAV_PATHS.SEARCH,
          hidden: !userHasPermission(user, PERMISSIONS.SECTION_SEARCH),
        },
        {
          label: formatMessage('SiteNavigation.Models'),
          value: showModelsBasedOnPermission(),
          hidden: !userHasPermission(user, PERMISSIONS.SECTION_MODELS),
        },
        {
          label: formatMessage('SiteNavigation.Insights'),
          value: NAV_PATHS.INSIGHTS,
          hidden: !userHasPermission(user, PERMISSIONS.SECTION_INSIGHTS),
        },
        {
          label: formatMessage('SiteNavigation.Admin'),
          value: NAV_PATHS.ADMIN,
          hidden: !userHasAdminPermission(user) || delegateMode,
        },
      ];
      return items.filter(i => !i.hidden);
    },
    [delegateMode, getContactHeaderTitle, getContactsMenuPath],
  );

  if (!user) {
    return null;
  }

  let home: string = NAV_PATHS.COMPARISONS;
  if (user.appConfig.mode === AppMode.CONTACT) {
    home = getContactsMenuPath(user);
  }

  const navigationItems = getLeftMenuItems(user);

  return (
    <>
      <SkipContentLink />
      <DelegateBanner />
      <AnnouncementBanner />

      <div className="header-v2" id="main-header">
        <div className="header-v2-inner">
          <div className="d-flex align-items-center header-left-items">
            <Link to={home} aria-label="CapIntel">
              <CapintelLogo className="capintel-logo logo-large" />
              <CapintelLogoSmall className="capintel-logo logo-small" />
            </Link>
            {isDesktop && <PrimaryNavigation items={navigationItems} className="navigation-v2" />}
          </div>
          <div className="d-flex align-items-center header-right-items">
            <Button
              variant="secondary"
              appearance="text"
              aria-label={formatMessage('SiteNavigation.Help')}
              data-testid="btn-help-button"
              icon="help-outline"
              onClick={openHelpLink}
            />
            {userHasPermission(user, PERMISSIONS.EXTERNAL_NOTIFICATION_CENTER) && (
              <Button variant="secondary" appearance="text" aria-label={formatMessage('AnnouncementBell.IconLabel')} icon="notification-bell" />
            )}
            {userHasPermission(user, PERMISSIONS.NOTIFICATION_CENTER) && <AnnouncementBell />}

            {isDesktop ? (
              <UserDropdown user={user} history={history} location={location} match={match} />
            ) : (
              <HeaderBurgerMenuContainer menuItems={navigationItems} user={user} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Header = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <HeaderV2 {...props} />;
  }
  return <LegacyHeader {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  contact: state.contacts.current,
  contextHouseholdId: state.households.contextHouseholdId,
  household: state.households.currentHousehold,
  delegateMode: selectDelegateMode(state),
  locale: state.intl.locale,
  user: state.auth.user,
});

export default withRouter(
  connect(mapStateToProps, {
    setLocale,
    logOpenHelpCenter,
  })(Header),
);
